import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsMore from 'highcharts/highcharts-more';
import { Tooltip, Whisper } from 'rsuite';
import infoIcon from '../../../../../../assets/icons/icon_info_gray.svg';
import { useElasticidadeCruzadaStore } from '../../ElasticidadeCruzada.store';
import styles from './styles.module.scss';

if (typeof HighchartsMore === 'function') {
  HighchartsMore(Highcharts);
}

const GAUGE_HEIGHT_PX = 240;
const GAUGE_CENTER = ['50%', '75%'] as const;
const GAUGE_SIZE = '110%';
const GAUGE_START_ANGLE = -90;
const GAUGE_END_ANGLE = 90;
const GAUGE_PLOT_BAND_THICKNESS = 30;
const GAUGE_DIAL_RADIUS = '75%';
const GAUGE_DIAL_COLOR = '#505050';
const GAUGE_DIAL_BASE_WIDTH = 15;
const GAUGE_DIAL_BASE_LENGTH = '0%';
const GAUGE_DIAL_REAR_LENGTH = '0%';
const GAUGE_DIAL_TOP_WIDTH = 1;
const GAUGE_PIVOT_RADIUS = 8;

const AVERAGE_ELASTICITY_TITLE = 'Elasticidade média do market share volume';
const AVERAGE_ELASTICITY_SUBTITLE = 'Considera apenas o market share volume, em relação aos concorrentes.';
const AVERAGE_ELASTICITY_TOOLTIP_PARAGRAPHS = [
  'Mostra o quanto o market share volume do produto tende a mudar quando o preço dele varia em relação aos concorrentes. Calculamos a média considerando todo o intervalo de CPI analisado.',
  'Esse índice considera apenas o market share volume. O market share valor não entra no cálculo e pode se comportar de forma diferente.',
];

const formatElasticityValue = (value: number): string => {
  if (!Number.isFinite(value)) {
    return '—';
  }
  let decimals = 2;
  if (value !== 0 && Math.abs(value) < 0.05) {
    decimals = 3;
  }
  // Evita "+0,00" quando o modelo devolve elasticidade > 0 mas < 0,005 (2 casas zeram).
  if (value !== 0 && Number(Math.abs(value).toFixed(2)) === 0) {
    decimals = 4;
  }
  const formatted = value.toFixed(decimals).replace('.', ',');
  return value >= 0 ? `+${formatted}` : formatted;
};

export const ElasticityGaugeChart = () => {
  const { elasticityResponse } = useElasticidadeCruzadaStore();

  if (!elasticityResponse) return null;

  const { elasticity, classification, gaugeConfig } = elasticityResponse;
  const gaugePointerValue = gaugeConfig.pointerValue;

  const chartOptions: Highcharts.Options = {
    chart: {
      type: 'gauge',
      backgroundColor: 'transparent',
      height: GAUGE_HEIGHT_PX,
      spacing: [0, 0, 0, 0],
    },
    title: {
      text: '',
    },
    credits: {
      enabled: false,
    },
    pane: {
      startAngle: GAUGE_START_ANGLE,
      endAngle: GAUGE_END_ANGLE,
      background: undefined,
      center: GAUGE_CENTER as unknown as string[],
      size: GAUGE_SIZE,
    },
    exporting: {
      enabled: false,
    },
    tooltip: {
      enabled: false,
    },
    yAxis: {
      min: gaugeConfig.min,
      max: gaugeConfig.max,
      tickLength: 0,
      tickWidth: 0,
      minorTickInterval: undefined,
      lineWidth: 0,
      labels: {
        enabled: false,
      },
      plotBands: gaugeConfig.plotBands.map(band => ({
        from: band.from,
        to: band.to,
        color: band.color,
        thickness: GAUGE_PLOT_BAND_THICKNESS,
      })),
    },
    plotOptions: {
      gauge: {
        wrap: false,
        dataLabels: {
          enabled: false,
        },
      },
    },
    series: [
      {
        type: 'gauge',
        name: 'Elasticidade',
        data: [gaugePointerValue],
        dial: {
          radius: GAUGE_DIAL_RADIUS,
          backgroundColor: GAUGE_DIAL_COLOR,
          baseWidth: GAUGE_DIAL_BASE_WIDTH,
          baseLength: GAUGE_DIAL_BASE_LENGTH,
          rearLength: GAUGE_DIAL_REAR_LENGTH,
          topWidth: GAUGE_DIAL_TOP_WIDTH,
        },
        pivot: {
          backgroundColor: GAUGE_DIAL_COLOR,
          radius: GAUGE_PIVOT_RADIUS,
        },
      },
    ],
  };

  return (
    <div className={styles.card}>
      <div className={styles.titleRow}>
        <h3 className={styles.title}>{AVERAGE_ELASTICITY_TITLE}</h3>
        <Whisper
          trigger="hover"
          placement="bottom"
          delayShow={200}
          speaker={
            <Tooltip className={styles.tooltip}>
              {AVERAGE_ELASTICITY_TOOLTIP_PARAGRAPHS.map(paragraph => (
                <p key={paragraph} className={styles.tooltipParagraph}>
                  {paragraph}
                </p>
              ))}
            </Tooltip>
          }
        >
          <button type="button" className={styles.infoButton} aria-label="Informação sobre a elasticidade média">
            <img src={infoIcon} alt="" />
          </button>
        </Whisper>
      </div>
      <div className={styles.chartContainer}>
        <HighchartsReact highcharts={Highcharts} options={chartOptions} />
      </div>
      <div className={styles.valueDisplay}>{formatElasticityValue(elasticity)}</div>
      <div className={styles.infoWrapper}>
        <p className={styles.classificationText}>Esse produto é {classification}</p>
        <p className={styles.description}>{AVERAGE_ELASTICITY_SUBTITLE}</p>
      </div>
    </div>
  );
};
